import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Lock,
  Bot,
  User,
  ChevronDown,
  ChevronRight,
  Trash2,
  ArrowRight,
  Layers,
  Cpu,
  Sparkles,
  Circle,
} from 'lucide-react';

// ==========================================
// ORIGIN BADGES
// ==========================================

function OriginBadge({ origin }) {
  if (origin === 'system') {
    return (
      <Badge variant="outline" className="text-[10px] gap-1">
        <Lock className="h-2.5 w-2.5" /> System
      </Badge>
    );
  }
  if (origin === 'service') {
    return (
      <Badge variant="outline" className="text-[10px] gap-1 border-purple-500/50 text-purple-400">
        <Cpu className="h-2.5 w-2.5" /> Service
      </Badge>
    );
  }
  if (origin?.startsWith('ai:')) {
    return (
      <Badge variant="outline" className="text-[10px] gap-1 border-blue-500/50 text-blue-400">
        <Bot className="h-2.5 w-2.5" /> AI
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px] gap-1 border-green-500/50 text-green-400">
      <User className="h-2.5 w-2.5" /> Custom
    </Badge>
  );
}

// ==========================================
// SERVICE SUBSCRIPTION CARD
// ==========================================

function ServiceCard({ service, enabled, onToggle, readOnly }) {
  const [expanded, setExpanded] = useState(false);
  const contribution = service.schema_contribution || {};
  const nodeCount = (contribution.node_types || []).length;
  const edgeCount = (contribution.edge_types || []).length;
  const dimensions = service.framework_definition?.dimensions || [];

  return (
    <Card className={`transition-colors ${!enabled ? 'opacity-50' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 text-sm font-medium hover:text-primary transition-colors"
              >
                {expanded ? (
                  <ChevronDown className="h-3.5 w-3.5 shrink-0" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 shrink-0" />
                )}
                {service.name}
              </button>
              <Badge variant="secondary" className="text-[10px]">
                {service.category}
              </Badge>
              {dimensions.length > 0 && (
                <span className="text-[10px] text-muted-foreground">
                  {dimensions.length} dimensions
                </span>
              )}
            </div>
            <p className="text-xs text-muted-foreground ml-5">
              {service.description}
            </p>
            {(nodeCount > 0 || edgeCount > 0) && (
              <div className="flex items-center gap-3 ml-5 mt-1">
                {nodeCount > 0 && (
                  <span className="text-[10px] text-muted-foreground">
                    +{nodeCount} node{nodeCount !== 1 ? 's' : ''}
                  </span>
                )}
                {edgeCount > 0 && (
                  <span className="text-[10px] text-muted-foreground">
                    +{edgeCount} edge{edgeCount !== 1 ? 's' : ''}
                  </span>
                )}
              </div>
            )}
          </div>
          <Switch
            checked={enabled}
            onCheckedChange={(checked) => onToggle?.(service.serviceId, checked)}
            disabled={readOnly}
          />
        </div>

        {expanded && (
          <div className="mt-3 ml-5 space-y-3">
            {/* Dimensions */}
            {dimensions.length > 0 && (
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                  Dimensions
                </Label>
                <div className="grid grid-cols-2 gap-1.5">
                  {dimensions.map((dim) => (
                    <div key={dim.id || dim.name} className="text-xs px-2 py-1 rounded bg-muted/50">
                      <span className="font-mono text-[10px] text-primary">
                        {dim.id || dim.name}
                      </span>
                      {dim.description && (
                        <span className="text-muted-foreground ml-1">
                          — {dim.description}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Schema contribution */}
            {(nodeCount > 0 || edgeCount > 0) && (
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                  Schema Contribution
                </Label>
                <div className="space-y-1">
                  {(contribution.node_types || []).map((nt) => (
                    <div key={nt.label} className="flex items-center gap-2 text-xs">
                      <Circle className="h-2 w-2 fill-blue-400 text-blue-400" />
                      <span className="font-mono text-blue-400">{nt.label}</span>
                      <span className="text-muted-foreground">
                        [{(nt.properties || []).join(', ')}]
                      </span>
                    </div>
                  ))}
                  {(contribution.edge_types || []).map((et) => (
                    <div key={`${et.from}-${et.type}-${et.to}`} className="flex items-center gap-1.5 text-xs">
                      <ArrowRight className="h-2 w-2 text-orange-400" />
                      <span className="text-muted-foreground">{et.from}</span>
                      <span className="font-mono text-orange-400">{et.type}</span>
                      <span className="text-muted-foreground">{et.to}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ==========================================
// SERVICES TAB
// ==========================================

function ServicesTab({ schema, services, onChange, readOnly }) {
  const subscriptions = schema.subscriptions || {};

  // Group services by category
  const grouped = useMemo(() => {
    const groups = { classify: [], extract: [], enrich: [], system: [] };
    for (const svc of services || []) {
      if (svc.serviceId === 'system:generate-schema') continue; // Hide meta-service
      const cat = svc.category || 'system';
      if (groups[cat]) groups[cat].push(svc);
    }
    return groups;
  }, [services]);

  // Build lookup: serviceId → enabled status
  const enabledMap = useMemo(() => {
    const map = {};
    for (const sub of subscriptions.classifiers || []) map[sub.serviceId] = sub.enabled;
    for (const sub of subscriptions.enrichers || []) map[sub.serviceId] = sub.enabled;
    for (const sub of subscriptions.system || []) map[sub.serviceId] = sub.enabled;
    return map;
  }, [subscriptions]);

  function handleToggle(serviceId, enabled) {
    const updated = { ...subscriptions };

    // Determine which subscription list to update
    if (serviceId.startsWith('classify:')) {
      const list = [...(updated.classifiers || [])];
      const idx = list.findIndex(s => s.serviceId === serviceId);
      if (idx >= 0) {
        list[idx] = { ...list[idx], enabled };
      } else {
        list.push({ serviceId, enabled });
      }
      updated.classifiers = list;
    } else if (serviceId.startsWith('enrich:')) {
      const list = [...(updated.enrichers || [])];
      const idx = list.findIndex(s => s.serviceId === serviceId);
      if (idx >= 0) {
        list[idx] = { ...list[idx], enabled };
      } else {
        list.push({ serviceId, enabled });
      }
      updated.enrichers = list;
    } else if (serviceId.startsWith('system:')) {
      const list = [...(updated.system || [])];
      const idx = list.findIndex(s => s.serviceId === serviceId);
      if (idx >= 0) {
        list[idx] = { ...list[idx], enabled };
      } else {
        list.push({ serviceId, enabled });
      }
      updated.system = list;
    }

    onChange?.({ ...schema, subscriptions: updated });
  }

  const categoryLabels = {
    classify: 'Classifiers',
    extract: 'Extractors',
    enrich: 'Enrichers',
    system: 'System',
  };

  const categoryDescriptions = {
    classify: 'LLM intelligence that categorizes and scores content',
    extract: 'Modality conversion — text, image, audio',
    enrich: 'Graph-aware post-classification processing',
    system: 'Platform infrastructure services',
  };

  return (
    <div className="space-y-6">
      {Object.entries(grouped).map(([category, svcs]) => {
        if (svcs.length === 0) return null;
        return (
          <div key={category}>
            <div className="mb-2">
              <h3 className="text-sm font-semibold">{categoryLabels[category]}</h3>
              <p className="text-xs text-muted-foreground">{categoryDescriptions[category]}</p>
            </div>
            <div className="space-y-2">
              {svcs.map((svc) => (
                <ServiceCard
                  key={svc.serviceId}
                  service={svc}
                  enabled={enabledMap[svc.serviceId] ?? false}
                  onToggle={handleToggle}
                  readOnly={readOnly}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ==========================================
// GRAPH SCHEMA TAB (computed + custom)
// ==========================================

function GraphSchemaTab({ schema, services, onDeleteCustomNode, onDeleteCustomEdge, readOnly }) {
  // Compute node/edge types from subscriptions (client-side mirror of backend logic)
  const computed = useMemo(() => {
    if (!schema.subscriptions || !services?.length) {
      return { node_types: [], edge_types: [] };
    }

    const nodeMap = new Map();
    const edgeMap = new Map();
    const allSubs = [
      ...(schema.subscriptions.classifiers || []),
      ...(schema.subscriptions.enrichers || []),
      ...(schema.subscriptions.system || []),
    ];

    for (const sub of allSubs) {
      if (!sub.enabled) continue;
      const service = services.find(s => s.serviceId === sub.serviceId);
      if (!service?.schema_contribution) continue;

      for (const nt of service.schema_contribution.node_types || []) {
        if (!nodeMap.has(nt.label)) {
          nodeMap.set(nt.label, {
            name: nt.label,
            properties: nt.properties || [],
            origin: 'service',
            serviceId: sub.serviceId,
          });
        }
      }
      for (const et of service.schema_contribution.edge_types || []) {
        const key = `${et.from}-${et.type}-${et.to}`;
        if (!edgeMap.has(key)) {
          edgeMap.set(key, {
            name: et.type,
            from: et.from,
            to: et.to,
            properties: et.properties || [],
            origin: 'service',
            serviceId: sub.serviceId,
          });
        }
      }
    }

    return {
      node_types: Array.from(nodeMap.values()),
      edge_types: Array.from(edgeMap.values()),
    };
  }, [schema.subscriptions, services]);

  // System defaults
  const systemNodes = [
    { name: 'Tenant', properties: ['id', 'name'], origin: 'system' },
    { name: 'Account', properties: ['id', 'email', 'tenantId'], origin: 'system' },
    { name: 'ContentAtom', properties: ['id', 'snippet', 'content_type', 'source_modality'], origin: 'system' },
    { name: 'GraphSpace', properties: ['id', 'name'], origin: 'system' },
  ];

  const systemEdges = [
    { name: 'BELONGS_TO_TENANT', from: 'Account', to: 'Tenant', origin: 'system' },
    { name: 'IN_TENANT', from: 'ContentAtom', to: 'Tenant', origin: 'system' },
    { name: 'IN_SPACE', from: 'ContentAtom', to: 'GraphSpace', origin: 'system' },
    { name: 'AUTHORED', from: 'Account', to: 'ContentAtom', origin: 'system' },
    { name: 'MEMBER_OF', from: 'Account', to: 'GraphSpace', origin: 'system' },
    { name: 'DERIVED_FROM', from: 'ContentAtom', to: 'ContentAtom', origin: 'system' },
  ];

  const customNodes = schema.custom_node_types || [];
  const customEdges = schema.custom_edge_types || [];

  const totalNodes = systemNodes.length + computed.node_types.length + customNodes.length;
  const totalEdges = systemEdges.length + computed.edge_types.length + customEdges.length;

  return (
    <div className="space-y-6">
      {/* Node Types */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-sm font-semibold">Node Types</h3>
          <Badge variant="secondary" className="text-[10px]">{totalNodes}</Badge>
        </div>

        <div className="space-y-1.5">
          {/* System nodes */}
          {systemNodes.map((n) => (
            <div key={n.name} className="flex items-center justify-between px-3 py-2 rounded-lg border bg-card">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{n.name}</span>
                <OriginBadge origin="system" />
              </div>
              <span className="text-xs text-muted-foreground">{n.properties?.join(', ')}</span>
            </div>
          ))}

          {/* Service-computed nodes */}
          {computed.node_types.map((n) => (
            <div key={n.name} className="flex items-center justify-between px-3 py-2 rounded-lg border bg-card border-purple-500/20">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{n.name}</span>
                <OriginBadge origin="service" />
                <span className="text-[10px] text-muted-foreground font-mono">{n.serviceId}</span>
              </div>
              <span className="text-xs text-muted-foreground">{n.properties?.join(', ')}</span>
            </div>
          ))}

          {/* Custom nodes */}
          {customNodes.map((n) => (
            <div key={n.name} className="flex items-center justify-between px-3 py-2 rounded-lg border bg-card border-green-500/20">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">{n.name}</span>
                <OriginBadge origin={n.origin || 'user'} />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{n.properties?.join(', ')}</span>
                {!readOnly && (
                  <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onDeleteCustomNode?.(n.name)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edge Types */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <h3 className="text-sm font-semibold">Edge Types</h3>
          <Badge variant="secondary" className="text-[10px]">{totalEdges}</Badge>
        </div>

        <div className="space-y-1.5">
          {/* System edges */}
          {systemEdges.map((e) => (
            <div key={e.name} className="flex items-center justify-between px-3 py-2 rounded-lg border bg-card">
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-muted-foreground">{e.from}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <span className="text-sm font-medium">{e.name}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs font-mono text-muted-foreground">{e.to}</span>
                <OriginBadge origin="system" />
              </div>
            </div>
          ))}

          {/* Service-computed edges */}
          {computed.edge_types.map((e) => (
            <div key={`${e.from}-${e.name}-${e.to}`} className="flex items-center justify-between px-3 py-2 rounded-lg border bg-card border-purple-500/20">
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-muted-foreground">{e.from}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <span className="text-sm font-medium">{e.name}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs font-mono text-muted-foreground">{e.to}</span>
                <OriginBadge origin="service" />
                <span className="text-[10px] text-muted-foreground font-mono">{e.serviceId}</span>
              </div>
            </div>
          ))}

          {/* Custom edges */}
          {customEdges.map((e) => (
            <div key={`${e.from}-${e.name}-${e.to}`} className="flex items-center justify-between px-3 py-2 rounded-lg border bg-card border-green-500/20">
              <div className="flex items-center gap-2">
                <span className="text-xs font-mono text-muted-foreground">{e.from}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <span className="text-sm font-medium">{e.name}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <span className="text-xs font-mono text-muted-foreground">{e.to}</span>
                <OriginBadge origin={e.origin || 'user'} />
              </div>
              {!readOnly && (
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onDeleteCustomEdge?.(e.name)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ==========================================
// PIPELINE TAB
// ==========================================

function PipelineView({ config, onChange, readOnly }) {
  const phases = [
    { key: 'commit', label: 'Commit', description: 'Store raw atom', alwaysOn: true },
    { key: 'apply_frameworks', label: 'Classify', description: 'Run subscribed classifiers' },
    { key: 'contextualize', label: 'Contextualize', description: 'Graph-aware enrichment' },
    { key: 'prioritize', label: 'Prioritize', description: 'Importance scoring' },
    { key: 'heartbeat', label: 'Heartbeat', description: 'Consciousness layer' },
  ];

  return (
    <div className="space-y-2">
      {phases.map((phase, idx) => (
        <div key={phase.key} className="flex items-center gap-3">
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-mono">
            {idx + 1}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{phase.label}</span>
              <span className="text-xs text-muted-foreground">{phase.description}</span>
            </div>
          </div>
          {phase.alwaysOn ? (
            <Badge variant="outline" className="text-[10px]">Always On</Badge>
          ) : (
            <Switch
              checked={config?.[phase.key] ?? false}
              onCheckedChange={(checked) => onChange?.({ ...config, [phase.key]: checked })}
              disabled={readOnly}
            />
          )}
        </div>
      ))}
      <div className="flex items-center gap-3 mt-3 pt-3 border-t">
        <span className="text-xs text-muted-foreground">Parallel Classifiers</span>
        <Switch
          checked={config?.parallel_frameworks ?? true}
          onCheckedChange={(checked) => onChange?.({ ...config, parallel_frameworks: checked })}
          disabled={readOnly}
        />
      </div>
    </div>
  );
}

// ==========================================
// EXTRACTION TAB
// ==========================================

function ExtractionView({ schema, onChange, readOnly }) {
  const subscriptions = schema.subscriptions || {};
  const extraction = subscriptions.extraction || {};
  const modalities = extraction.modalities || schema.supported_modalities || ['TEXT'];
  const entityTypes = extraction.entity_types || ['concept_extraction', 'people_extraction'];

  const allModalities = ['TEXT', 'IMAGE', 'AUDIO', 'PDF', 'VIDEO'];
  const allEntityTypes = [
    { key: 'concept_extraction', label: 'Extract Concepts' },
    { key: 'people_extraction', label: 'Extract People' },
  ];

  function updateExtraction(updates) {
    const updated = {
      ...schema,
      subscriptions: {
        ...subscriptions,
        extraction: {
          ...extraction,
          ...updates,
        },
      },
      // Keep supported_modalities in sync for backward compat
      supported_modalities: updates.modalities || modalities,
    };
    onChange?.(updated);
  }

  return (
    <div className="space-y-4">
      <div className="space-y-3">
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Entity Extraction
        </Label>
        {allEntityTypes.map((et) => (
          <div key={et.key} className="flex items-center justify-between">
            <span className="text-sm">{et.label}</span>
            <Switch
              checked={entityTypes.includes(et.key)}
              onCheckedChange={(checked) => {
                const next = checked
                  ? [...entityTypes, et.key]
                  : entityTypes.filter(t => t !== et.key);
                updateExtraction({ entity_types: next });
              }}
              disabled={readOnly}
            />
          </div>
        ))}
      </div>

      <div className="space-y-3 pt-3 border-t">
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Supported Modalities
        </Label>
        <div className="flex flex-wrap gap-2">
          {allModalities.map((mod) => {
            const active = modalities.includes(mod);
            return (
              <button
                key={mod}
                onClick={() => {
                  if (readOnly) return;
                  const next = active
                    ? modalities.filter(m => m !== mod)
                    : [...modalities, mod];
                  updateExtraction({ modalities: next });
                }}
                disabled={readOnly}
                className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                  active
                    ? 'bg-primary/15 border-primary/50 text-primary'
                    : 'bg-muted/30 border-transparent text-muted-foreground hover:bg-muted/50'
                } ${readOnly ? 'cursor-default' : 'cursor-pointer'}`}
              >
                {mod}
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-3 pt-3 border-t">
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Derived Atoms
        </Label>
        <div className="flex items-center justify-between">
          <span className="text-sm">Create Derived Atoms</span>
          <Switch
            checked={schema.derived_atom_config?.enabled ?? true}
            onCheckedChange={(checked) => {
              onChange?.({
                ...schema,
                derived_atom_config: { ...schema.derived_atom_config, enabled: checked },
              });
            }}
            disabled={readOnly}
          />
        </div>
      </div>
    </div>
  );
}

// ==========================================
// LEGACY FALLBACK — for old schemas without subscriptions
// ==========================================

function LegacyFrameworkCard({ framework, onToggle, onDelete, readOnly }) {
  const [expanded, setExpanded] = useState(false);
  const isSystem = framework.origin === 'system';

  return (
    <Card className={`transition-colors ${!framework.enabled ? 'opacity-50' : ''}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <button
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 text-sm font-medium hover:text-primary transition-colors"
              >
                {expanded ? <ChevronDown className="h-3.5 w-3.5 shrink-0" /> : <ChevronRight className="h-3.5 w-3.5 shrink-0" />}
                {framework.name}
              </button>
              <OriginBadge origin={framework.origin} />
              <Badge variant="secondary" className="text-[10px]">{framework.category}</Badge>
            </div>
            <p className="text-xs text-muted-foreground ml-5">{framework.description}</p>
            <p className="text-[10px] text-muted-foreground ml-5 mt-0.5">{framework.dimensions.length} dimensions</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {!readOnly && !isSystem && (
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDelete?.(framework.id)}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            )}
            <Switch
              checked={framework.enabled}
              onCheckedChange={(checked) => onToggle?.(framework.id, checked)}
              disabled={readOnly}
            />
          </div>
        </div>
        {expanded && (
          <div className="mt-3 ml-5 grid grid-cols-2 gap-1.5">
            {framework.dimensions.map((dim) => (
              <div key={dim.name} className="text-xs px-2 py-1 rounded bg-muted/50">
                <span className="font-mono text-[10px] text-primary">{dim.name}</span>
                {dim.description && <span className="text-muted-foreground ml-1">— {dim.description}</span>}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LegacyEditor({ schema, onChange, readOnly }) {
  function handleFrameworkToggle(frameworkId, enabled) {
    onChange?.({
      ...schema,
      frameworks: schema.frameworks.map(f => f.id === frameworkId ? { ...f, enabled } : f),
    });
  }
  function handleFrameworkDelete(frameworkId) {
    onChange?.({
      ...schema,
      frameworks: schema.frameworks.filter(f => f.id !== frameworkId),
    });
  }
  function handleNodeTypeDelete(name) {
    onChange?.({ ...schema, node_types: schema.node_types.filter(n => n.name !== name) });
  }
  function handleEdgeTypeDelete(name) {
    onChange?.({ ...schema, edge_types: schema.edge_types.filter(e => e.name !== name) });
  }

  return (
    <Tabs defaultValue="frameworks" className="w-full">
      <TabsList className="w-full grid grid-cols-5">
        <TabsTrigger value="frameworks">Frameworks</TabsTrigger>
        <TabsTrigger value="nodes">Node Types</TabsTrigger>
        <TabsTrigger value="edges">Edge Types</TabsTrigger>
        <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
        <TabsTrigger value="extraction">Extraction</TabsTrigger>
      </TabsList>

      <TabsContent value="frameworks" className="space-y-3">
        <div className="rounded-md bg-yellow-500/10 p-3 text-xs text-yellow-500 mb-3">
          This schema uses the legacy framework format. It will continue to work, but new schemas use the service subscription model.
        </div>
        {schema.frameworks?.map((framework) => (
          <LegacyFrameworkCard
            key={framework.id}
            framework={framework}
            onToggle={handleFrameworkToggle}
            onDelete={handleFrameworkDelete}
            readOnly={readOnly}
          />
        ))}
      </TabsContent>

      <TabsContent value="nodes" className="space-y-2">
        {schema.node_types?.map((nodeType) => (
          <div key={nodeType.name} className="flex items-center justify-between px-3 py-2 rounded-lg border bg-card">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{nodeType.name}</span>
              <OriginBadge origin={nodeType.origin} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{nodeType.properties?.join(', ')}</span>
              {!readOnly && nodeType.origin !== 'system' && (
                <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleNodeTypeDelete(nodeType.name)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </TabsContent>

      <TabsContent value="edges" className="space-y-2">
        {schema.edge_types?.map((edgeType) => (
          <div key={edgeType.name} className="flex items-center justify-between px-3 py-2 rounded-lg border bg-card">
            <div className="flex items-center gap-2">
              <span className="text-xs font-mono text-muted-foreground">{edgeType.from}</span>
              <ArrowRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-sm font-medium">{edgeType.name}</span>
              <ArrowRight className="h-3 w-3 text-muted-foreground" />
              <span className="text-xs font-mono text-muted-foreground">{edgeType.to}</span>
              <OriginBadge origin={edgeType.origin} />
            </div>
            {!readOnly && edgeType.origin !== 'system' && (
              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleEdgeTypeDelete(edgeType.name)}>
                <Trash2 className="h-3 w-3" />
              </Button>
            )}
          </div>
        ))}
      </TabsContent>

      <TabsContent value="pipeline">
        <Card>
          <CardContent className="p-4">
            <PipelineView
              config={schema.pipeline_config}
              onChange={(config) => onChange?.({ ...schema, pipeline_config: config })}
              readOnly={readOnly}
            />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="extraction">
        <Card>
          <CardContent className="p-4">
            <ExtractionView schema={schema} onChange={onChange} readOnly={readOnly} />
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}

// ==========================================
// MAIN SCHEMA EDITOR
// ==========================================

/**
 * SchemaEditor — Shared component for viewing/editing app schemas.
 * Used by both CreateAppWizard (step 3) and SchemaPage.
 *
 * New schemas use the service subscription model (4 tabs: Services, Graph Schema, Pipeline, Extraction).
 * Legacy schemas without subscriptions fall back to the old framework-based editor.
 *
 * @param {object} schema - The app schema object
 * @param {function} onChange - Called with updated schema
 * @param {boolean} readOnly - Disable editing
 * @param {object[]} services - Available platform services from service catalog API
 */
// Named exports for use by SchemaPage (top-level tabs)
export { ServicesTab, GraphSchemaTab, PipelineView, ExtractionView };

export default function SchemaEditor({ schema, onChange, readOnly = false, services = [] }) {
  // Count enabled services for tab badge — must be before any early returns
  const enabledCount = useMemo(() => {
    if (!schema?.subscriptions) return 0;
    const subs = schema.subscriptions;
    return [
      ...(subs.classifiers || []),
      ...(subs.enrichers || []),
      ...(subs.system || []),
    ].filter(s => s.enabled).length;
  }, [schema?.subscriptions]);

  if (!schema) return null;

  // Legacy schemas without subscriptions use the old editor
  const hasSubscriptions = !!schema.subscriptions;
  if (!hasSubscriptions && services.length === 0) {
    return <LegacyEditor schema={schema} onChange={onChange} readOnly={readOnly} />;
  }

  function handleDeleteCustomNode(name) {
    onChange?.({
      ...schema,
      custom_node_types: (schema.custom_node_types || []).filter(n => n.name !== name),
    });
  }

  function handleDeleteCustomEdge(name) {
    onChange?.({
      ...schema,
      custom_edge_types: (schema.custom_edge_types || []).filter(e => e.name !== name),
    });
  }

  function handlePipelineChange(config) {
    onChange?.({ ...schema, pipeline_config: config });
  }

  return (
    <Tabs defaultValue="services" className="w-full">
      <TabsList className="w-full grid grid-cols-4">
        <TabsTrigger value="services" className="gap-1.5">
          <Layers className="h-3.5 w-3.5" />
          Services
          {enabledCount > 0 && (
            <Badge variant="secondary" className="text-[10px] ml-1">{enabledCount}</Badge>
          )}
        </TabsTrigger>
        <TabsTrigger value="schema">Graph Schema</TabsTrigger>
        <TabsTrigger value="pipeline">Pipeline</TabsTrigger>
        <TabsTrigger value="extraction">Extraction</TabsTrigger>
      </TabsList>

      <TabsContent value="services">
        <ServicesTab
          schema={schema}
          services={services}
          onChange={onChange}
          readOnly={readOnly}
        />
      </TabsContent>

      <TabsContent value="schema">
        <GraphSchemaTab
          schema={schema}
          services={services}
          onDeleteCustomNode={handleDeleteCustomNode}
          onDeleteCustomEdge={handleDeleteCustomEdge}
          readOnly={readOnly}
        />
      </TabsContent>

      <TabsContent value="pipeline">
        <Card>
          <CardContent className="p-4">
            <PipelineView
              config={schema.pipeline_config}
              onChange={handlePipelineChange}
              readOnly={readOnly}
            />
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="extraction">
        <Card>
          <CardContent className="p-4">
            <ExtractionView schema={schema} onChange={onChange} readOnly={readOnly} />
          </CardContent>
        </Card>
      </TabsContent>
    </Tabs>
  );
}
